import React, {useEffect, useRef, useState} from 'react';
import './index.css';
import {combineTreatments} from '../../../../../utils/skincareUtils';
import classnames from 'classnames';
import 'react-multi-carousel/lib/styles.css';
import _get from 'lodash/get';
import i18n from '../../../../../utils/i18n';
import globals from '../../../../../utils/config/globals';
import {useSelector} from 'react-redux';
import smbUtils from '../../../../../utils/smbUtils';
import {deviceOptions} from '../../config/utils/optionsUtils';
import storeUtils from '../../../../../utils/storeUtils';
import PreviewToolBar from './PreviewToolBar';
import SimulationDisplaySection from './SimulationDisplaySection';
import {Modal} from 'react-bootstrap';
import useWindowHeight from '../../../../../components/hook/useWindowHeight';
import useWindowWidth from '../../../../../components/hook/useWindowWidth';
import skincareProDao from '../../../../../dao/skincareProDao';
import useSkincare from '../../../../../components/hook/useSkincare';
import langUtils from '../../../../../utils/langUtils';

export default function SkincareSimulationPreview({
  config,
  commonData = {},
  isPremiumPlan,
}) {
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {customerId} = smbUtils.customerInfo(auth);
  const [facialZones, setFacialZones] = useState([]);
  const slider = useRef();
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [selectedFacialZone, setSelectedFacialZones] = useState('');
  const [selectTreatment, setSelectTreatment] = useState('');
  const [
    customSimulationTreatmentData,
    setCustomSimulationTreatmentData,
  ] = useState({});
  const [language, setLanguage] = useState(
    globals.languages2.find(l => l.i18n === i18n.language)
  );
  const [device, setDevice] = useState(
    deviceOptions
      .filter(o => o.value !== 'mobile')
      .find(
        option =>
          option.value === storeUtils.getUiConfigSettingDevice(customerId)
      ) || deviceOptions[0]
  );
  const [fullScreen, setFullScreen] = useState(false);
  const isLandscape = _get(device, 'value') === deviceOptions[1].value;
  const windowHeight = useWindowHeight();
  const windowWidth = useWindowWidth();
  const {isVersionAbove2AndHd} = useSkincare(auth);

  function calculateTransform(windowWidth, windowHeight, isLandscape) {
    return `scale(${
      windowWidth > windowHeight
        ? isLandscape
          ? windowHeight * 0.0027
          : windowHeight * 0.0018
        : windowWidth * 0.0021
    })`;
  }
  
  const getCustomTreatmentName = (ts, lang) => {
    const langKey = langUtils.getMappedLangKey(lang);
    return _get(
      customSimulationTreatmentData,
      `${langKey}.${ts.type}.${ts.value}`,
      null
    );
  };

  useEffect(() => {
    const zones =
      _get(config, 'facialZones', []).length > 0
        ? _get(config, 'facialZones', []).filter(f => !f.disabled)
        : [];
    const treatments = _get(config, 'treatmentOptions', []);
    const array = zones.map(r => r.value);
    setFacialZones(zones);
    let treatmentsArray =
      zones.length > 0
        ? combineTreatments(treatments, isPremiumPlan, array)
        : [];

    const customTreatments = treatmentsArray
      .filter(t => !t.disabled && array.includes(t.type))
      .map(treatment => ({
        ...treatment,
        customName: getCustomTreatmentName(treatment, language.i18n),
      }));

    setTreatmentOptions(customTreatments);
  }, [config, customSimulationTreatmentData, language]);

  useEffect(() => {
    if (!isVersionAbove2AndHd) return;

    const fetchAppSettings = async () => {
      try {
        const [appSettingsConfig] = await Promise.all([
          skincareProDao.getCustomerSetting({
            type: 'ScpAppSettings',
          }),
        ]);
        if (appSettingsConfig) {
          const customTreatmentName = _get(
            appSettingsConfig,
            'editing_revision.data.custom_simulation_treatment_name',
            {}
          );
          setCustomSimulationTreatmentData(customTreatmentName);
        }
      } catch (error) {
        console.error(
          'Failed to fetch app settings for simulation treatment names:',
          error
        );
      }
    };

    fetchAppSettings();
  }, []);

  useEffect(() => {
    if (facialZones.length > 0) {
      if (!facialZones.map(r => r.value).includes(selectedFacialZone.value)) {
        setSelectedFacialZones(facialZones[0]);
      }
    }
    if (treatmentOptions.length > 0) {
      const index = treatmentOptions.findIndex(
        r => r.value === selectTreatment.value
      );
      if (index < 0) {
        setSelectTreatment(treatmentOptions[0]);
        slider && slider.current && slider.current.goToSlide(0);
      } else {
        slider && slider.current && slider.current.goToSlide(index);
      }
    }
  }, [facialZones, treatmentOptions]);

  return (
    treatmentOptions.length > 0 && (
      <div className="simulationpreview-container">
        <PreviewToolBar
          language={language}
          setLanguage={setLanguage}
          setIsZoomed={setFullScreen}
          device={device}
          setDevice={setDevice}
        />
        <div
          className={classnames({
            'simulationpreview-model': true,
            'simulationpreview-model-landscape': isLandscape,
          })}
        >
          <SimulationDisplaySection
            commonData={commonData}
            facialZones={facialZones}
            selectedFacialZone={selectedFacialZone}
            setSelectedFacialZones={setSelectedFacialZones}
            treatmentOptions={treatmentOptions}
            selectTreatment={selectTreatment}
            setSelectTreatment={setSelectTreatment}
            isLandscape={isLandscape}
            language={language}
            slider={slider}
          />
        </div>
        <Modal
          size="sm"
          show={fullScreen}
          onHide={() => {
            setFullScreen(false);
          }}
          centered={true}
          dialogClassName="fullscreen-modal"
        >
          <Modal.Body className="fullscreen-modal-overlay">
            <div className="fullscreen-center-wrapper">
              <div className="simulation_preview-module-expand">
                <div
                  className={classnames({
                    'simulationpreview-model': true,
                    'simulationpreview-model-landscape': isLandscape,
                  })}
                  style={{
                    transform: calculateTransform(
                      windowWidth,
                      windowHeight,
                      isLandscape
                    ),
                    transformOrigin: 'center center',
                  }}
                >
                  <SimulationDisplaySection
                    commonData={commonData}
                    facialZones={facialZones}
                    selectedFacialZone={selectedFacialZone}
                    setSelectedFacialZones={setSelectedFacialZones}
                    treatmentOptions={treatmentOptions}
                    selectTreatment={selectTreatment}
                    setSelectTreatment={setSelectTreatment}
                    isLandscape={isLandscape}
                    language={language}
                    slider={slider}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    )
  );
}
